import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { CALENDLY_URL } from "../data/content";
import { openCalendlyPopup } from "../integrations/calendly";

export const Parallax = ({ children, speed = 30, className = "" }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
    return (
        <motion.div ref={ref} style={{ y }} className={className}>
            {children}
        </motion.div>
    );
};

export const Reveal = ({ children, delay = 0, y = 26, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-70px" }}
        transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        {children}
    </motion.div>
);

export const Eyebrow = ({ children, dark = false }) => (
    <div className="flex items-center gap-3" data-testid="section-eyebrow">
        <span className={`h-px w-8 ${dark ? "bg-gold/70" : "bg-gold"}`} />
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
            {children}
        </span>
    </div>
);

export const SectionHead = ({ eyebrow, title, copy, dark = false, align = "left" }) => (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
        <div className={align === "center" ? "flex justify-center" : ""}>
            <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
        </div>
        <h2
            className={`mt-4 font-serif text-2xl uppercase leading-[1.12] tracking-tight sm:text-3xl lg:text-4xl ${
                dark ? "text-cream" : "text-ink"
            }`}
        >
            {title}
        </h2>
        {copy && (
            <p className={`mt-4 text-sm leading-relaxed sm:text-base ${dark ? "text-[#B9B6C0]" : "text-mute"}`}>
                {copy}
            </p>
        )}
    </div>
);

export const PrimaryCTA = ({ children, href = CALENDLY_URL, testId, dark = false, className = "" }) => (
    <a
        data-testid={testId}
        href={href}
        onClick={href === CALENDLY_URL ? (e) => { e.preventDefault(); openCalendlyPopup(); } : undefined}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={`btn-arrow group inline-flex items-center gap-3 rounded-full px-7 py-3.5 font-sans text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
            dark
                ? "bg-cream text-ink hover:bg-blush"
                : "bg-ink text-cream hover:bg-charcoal"
        } ${className}`}
    >
        {children}
        <ArrowRight className="btn-arrow-icon h-4 w-4 text-gold" strokeWidth={1.75} />
    </a>
);

export const GhostCTA = ({ children, href, testId, dark = false }) => (
    <a
        data-testid={testId}
        href={href}
        className={`btn-arrow group inline-flex items-center gap-3 rounded-full border px-7 py-3.5 font-sans text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
            dark
                ? "border-cream/25 text-cream hover:border-gold/60 hover:text-gold"
                : "border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-cream"
        }`}
    >
        {children}
        <ArrowRight className="btn-arrow-icon h-4 w-4" strokeWidth={1.75} />
    </a>
);

export const CheckItem = ({ children, dark = false }) => (
    <div className="flex items-center gap-2.5">
        <span
            className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                dark ? "border-gold/50 bg-gold/10" : "border-gold/60 bg-blush"
            }`}
        >
            <Check className="h-3 w-3 text-gold" strokeWidth={2.5} />
        </span>
        <span className={`font-sans text-[13px] ${dark ? "text-cream/85" : "text-mute"}`}>{children}</span>
    </div>
);

export const Chip = ({ children, dark = false, className = "" }) => (
    <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${
            dark
                ? "border-cream/15 bg-charcoal/80 text-cream/90"
                : "border-linen bg-ivory/90 text-ink shadow-[0_8px_20px_rgba(17,17,19,0.07)]"
        } ${className}`}
    >
        {children}
    </span>
);

export const Avatar = ({ initials = "S", tone = "bg-blush text-ink" }) => (
    <span className={`flex h-8 w-8 items-center justify-center rounded-full font-serif text-sm ${tone}`}>
        {initials}
    </span>
);

export const FlowDot = ({ dark = false }) => (
    <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-gold" : "bg-rose"}`} />
);

export const ArrowDown = ({ dark = false, className = "" }) => (
    <svg viewBox="0 0 12 18" className={`h-4 w-3 ${className}`} fill="none">
        <path
            d="M6 1v14m0 0l-4.5-4.5M6 15l4.5-4.5"
            stroke={dark ? "#CBA368" : "#D8A7A1"}
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const Phone = ({ children, className = "", screen = "bg-[#FBF7F1]" }) => (
    <div className={`relative rounded-[2.1rem] bg-[#151517] p-[5px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] ${className}`}>
        <div className="absolute -right-[1px] top-24 h-10 w-[2px] rounded bg-[#2c2c30]" />
        <div className={`relative overflow-hidden rounded-[1.7rem] ${screen}`}>
            <div className="absolute left-1/2 top-2 z-20 h-[14px] w-[62px] -translate-x-1/2 rounded-full bg-[#151517]" />
            {children}
        </div>
    </div>
);

export const StatusBar = ({ dark = false }) => (
    <div className={`flex items-center justify-between px-5 pb-1 pt-3 font-mono text-[9px] tracking-widest ${dark ? "text-cream/70" : "text-ink/60"}`}>
        <span>9:41</span>
        <div className="flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-3 rounded-[2px] border border-current" />
            <span>5G</span>
        </div>
    </div>
);
